import type { SortingState, Table, Updater } from '@tanstack/react-table';
import { format } from 'date-fns';
import * as FileSaver from 'file-saver';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Controller as TabsController,
  ListingPageHeader,
  ModalTab,
} from 'components/Shared';
import { DataTableViewOptions } from './components/DataTable/data-table-view-options';
import { StatusLegend } from './components/StatusLegend';
import type {
  YardGateAppointmentType,
  YardGateCatalogs,
  YardGateChecklistAttachmentDto,
  YardGateChecklistAttachmentsDto,
  YardGateOption,
  YardGateTrackedStepColumn,
  YardGateAppointmentSortField,
  YardGateDateRange,
  YardGateMutableStatus,
  YardGateQueryFilters,
} from 'contracts/YardGate';
import type { ListedWarehouse } from 'contracts/Warehouses';
import type { YardJourneyFieldAnswerInput } from 'contracts/YardJourneyExecution';
import { useYardGate } from 'hooks/api/useYardGate';
import { useYardGateJourney } from 'hooks/api/useYardGateJourney';
import { useInterval } from 'hooks/useInterval';
import { Scaffold } from 'layouts';
import { apiErrorHandler, api, notify } from 'services';
import { Formatter } from 'utils';
import {
  DEFAULT_YARD_GATE_LOCAL_FILTERS,
  exportYardGateEntries,
  exportYardGateEntriesXlsx,
  filterDisplayTrackedStepColumns,
  getDefaultYardGateFilters,
  mapYardGateAppointment,
  validateYardGateDateRange,
  yardGateSnapshotFingerprint,
  YardGateEntry,
  YardGateFilters,
  YardGateWarehouseOption,
} from './adapter';
import { buildDockAttentionState, hasNewRemoteDockChange } from './dockAlert';
import type { DockAttentionState } from './dockAlert';
import { useDockAlertSound } from './useDockAlertSound';
import {
  EntriesTable,
  YARD_GATE_DEFAULT_COLUMN_PINNING,
} from './components/EntriesTable';
import { EntryFilters } from './components/EntryFilters';
import { CreateYardGateEntryInput, EntryForm } from './components/EntryForm';
import { ObservationModal } from './components/ObservationModal';
import { ChecklistModal } from './components/ChecklistModal';
import { StartJourneyModal } from './components/StartJourneyModal';
import { ExportMenu, type YardGateExportFormat } from './components/ExportMenu';
import * as S from './styles';

const emptyCatalogs: YardGateCatalogs = {
  orderTypes: [],
  carriers: [],
  customers: [],
  vehicleTypes: [],
  areas: [],
  docks: [],
};

const YARD_GATE_FILTERS_STORAGE_KEY = 'yard-gate-applied-filters';
const YARD_GATE_PAGE_SIZE_STORAGE_KEY = 'yard-gate-page-size';
const YARD_GATE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const SORT_FIELD_BY_COLUMN_ID: Record<string, YardGateAppointmentSortField> = {
  'Data Agendamento': 'date',
  'Motorista': 'driverName',
  'P. Chegada': 'entryTime',
  'H. Entrada': 'entryTime',
  'H. Saída': 'exitTime',
  'Placa': 'plate',
  'Doca Agendamento': 'dock',
  'Transp./Prest. Serviço': 'carrierName',
  'Cliente': 'customerName',
  'Ord. Coleta': 'collectionOrder',
  'Tipo de ordem': 'orderType',
};

const buildYardGateQuery = (
  filters: YardGateFilters,
  sorting: SortingState
): YardGateQueryFilters => {
  const sort = sorting[0];
  return {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    type:
      filters.appointmentType === 'ORDER' ||
      filters.appointmentType === 'STANDALONE'
        ? filters.appointmentType
        : undefined,
    registrationType: filters.appointmentType || undefined,
    statuses: filters.statuses,
    driverCpf: filters.driverCpf || undefined,
    driverName: filters.driverName || undefined,
    phone: filters.phone || undefined,
    orderNumber: filters.orderNumber || undefined,
    orderTypeId: filters.orderTypeId || undefined,
    carrierId: filters.carrierId || undefined,
    customerId: filters.customerId || undefined,
    warehouseIds: filters.warehouseIds,
    plate: filters.plate || undefined,
    dock: filters.dock || undefined,
    orderBy: sort ? SORT_FIELD_BY_COLUMN_ID[sort.id] : 'date',
    direction: sort?.desc ? 'desc' : 'asc',
  };
};

const getYardGateSortValue = (
  entry: YardGateEntry,
  field: YardGateAppointmentSortField
): string | null => {
  switch (field) {
    case 'date':
      return entry.scheduledAt;
    case 'driverName':
      return entry.driverName;
    case 'dock':
      return entry.dock;
    case 'entryTime':
      return entry.startTime;
    case 'exitTime':
      return entry.endTime;
    case 'plate':
      return entry.plate;
    case 'carrierName':
      return entry.carrierName;
    case 'customerName':
      return entry.customerName;
    case 'collectionOrder':
      return entry.orderNumber;
    case 'orderType':
      return entry.orderType;
  }
};

const getInitialYardGateFilters = (): YardGateFilters => {
  const defaults = getDefaultYardGateFilters();

  try {
    const stored = window.sessionStorage.getItem(YARD_GATE_FILTERS_STORAGE_KEY);
    if (!stored) return defaults;

    const parsed = JSON.parse(stored) as Partial<YardGateFilters>;
    const appointmentType = parsed.appointmentType;
    const stringFields = [
      'driverCpf',
      'driverName',
      'phone',
      'orderNumber',
      'plate',
      'dock',
    ] as const;
    const idFields = ['orderTypeId', 'carrierId', 'customerId'] as const;

    if (
      typeof parsed.dateFrom !== 'string' ||
      typeof parsed.dateTo !== 'string' ||
      (appointmentType !== undefined &&
        appointmentType !== '' &&
        appointmentType !== 'ORDER' &&
        appointmentType !== 'STANDALONE' &&
        appointmentType !== 'HORA_CERTA') ||
      !Array.isArray(parsed.statuses) ||
      !parsed.statuses.every((status) => typeof status === 'string') ||
      !Array.isArray(parsed.warehouseIds) ||
      !parsed.warehouseIds.every(
        (warehouseId) => typeof warehouseId === 'number'
      ) ||
      !stringFields.every((field) => typeof parsed[field] === 'string') ||
      !idFields.every(
        (field) =>
          parsed[field] === '' ||
          (typeof parsed[field] === 'number' && Number.isFinite(parsed[field]))
      )
    ) {
      return defaults;
    }

    const restored = {
      ...defaults,
      ...parsed,
      appointmentType: appointmentType ?? '',
    };
    return Object.keys(validateYardGateDateRange(restored)).length === 0
      ? restored
      : defaults;
  } catch {
    return defaults;
  }
};

const getInitialPageSize = (): number => {
  try {
    const stored = window.sessionStorage.getItem(
      YARD_GATE_PAGE_SIZE_STORAGE_KEY
    );
    const pageSize = Number(stored);
    return YARD_GATE_PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : 10;
  } catch {
    return 10;
  }
};

export const YARD_GATE_REFRESH_INTERVAL_MS = 60000;

const reportError = (error: unknown) =>
  notify(
    'error',
    apiErrorHandler(error).errorMessage || 'Erro ao executar a operação'
  );

export const YardGatePage = () => {
  const {
    listYardGateSnapshot,
    getYardGateChanges,
    getYardGateCatalogs,
    getYardGateAuthorizers,
    createYardGateAuthorizer,
    createStandaloneYardGateEntry,
    updateYardGateStatus,
    updateYardGateContact,
    updateYardGateDocks,
    updateYardGateObservation,
    listYardGateChecklistAttachments,
    uploadYardGateChecklistAttachments,
    getYardGateChecklistAttachmentUrl,
    deleteYardGateChecklistAttachment,
  } = useYardGate();
  const {
    getYardGateJourney,
    confirmYardGateJourney,
    completeYardGatePortariaStep,
  } = useYardGateJourney();
  const { soundEnabled, playDockAlert } = useDockAlertSound();
  const initialFilters = useMemo(getInitialYardGateFilters, []);
  const initialPageSize = useMemo(getInitialPageSize, []);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<YardGateFilters>(initialFilters);
  const [pagination, setPagination] = useState({
    total: 0,
    perPage: initialPageSize,
    page: 1,
    lastPage: 1,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        YARD_GATE_FILTERS_STORAGE_KEY,
        JSON.stringify(filters)
      );
    } catch {
      // Storage can be unavailable in private browsing contexts.
    }
  }, [filters]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        YARD_GATE_PAGE_SIZE_STORAGE_KEY,
        String(pagination.perPage)
      );
    } catch {
      // Storage can be unavailable in private browsing contexts.
    }
  }, [pagination.perPage]);

  const [snapshotRange, setSnapshotRange] = useState<YardGateDateRange | null>(
    null
  );
  const [snapshot, setSnapshot] = useState<YardGateEntry[]>([]);
  const snapshotRef = useRef<YardGateEntry[]>([]);
  const [trackedStepColumns, setTrackedStepColumns] = useState<
    YardGateTrackedStepColumn[]
  >([]);
  const [catalogs, setCatalogs] = useState<YardGateCatalogs>(emptyCatalogs);
  const [warehouseOptions, setWarehouseOptions] = useState<
    YardGateWarehouseOption[]
  >([]);
  const [authorizers, setAuthorizers] = useState<YardGateOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [busyEntryId, setBusyEntryId] = useState<number | string | null>(null);
  const [observationEntry, setObservationEntry] =
    useState<YardGateEntry | null>(null);
  const [checklistEntry, setChecklistEntry] = useState<YardGateEntry | null>(
    null
  );
  const [startJourneyEntry, setStartJourneyEntry] =
    useState<YardGateEntry | null>(null);
  const [tableInstance, setTableInstance] =
    useState<Table<YardGateEntry> | null>(null);
  const requestSequence = useRef(0);
  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);
  const sortingRef = useRef(sorting);
  const snapshotFingerprint = useRef<string | null>(null);
  const trackedStepColumnsFingerprint = useRef<string | null>(null);
  const snapshotRangeRef = useRef<YardGateDateRange | null>(null);
  const syncCursorRef = useRef<number | null>(null);
  const isBusyRef = useRef(false);
  const journeyMutationInFlightRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const dockAttentionRef = useRef<DockAttentionState | null>(null);
  const soundEnabledRef = useRef(soundEnabled);
  const playDockAlertRef = useRef(playDockAlert);

  const visibleEntries = snapshot;

  const rememberTableInstance = useCallback((table: Table<YardGateEntry>) => {
    setTableInstance((current) => (current === table ? current : table));
  }, []);

  const loadSnapshot = useCallback(
    async (
      range: YardGateDateRange,
      options?: {
        silent?: boolean;
        suppressDockAlert?: boolean;
        filters?: YardGateFilters;
        page?: number;
        limit?: number;
      }
    ): Promise<boolean> => {
      const silent = options?.silent ?? false;
      const suppressDockAlert = options?.suppressDockAlert ?? false;
      const sequence = requestSequence.current + 1;
      requestSequence.current = sequence;
      if (!silent) setIsLoading(true);
      try {
        const activeFilters = options?.filters ?? filtersRef.current;
        const query = buildYardGateQuery(activeFilters, sortingRef.current);
        query.dateFrom = range.dateFrom;
        query.dateTo = range.dateTo;
        const requestedPage = options?.page ?? paginationRef.current.page;
        const requestedLimit = options?.limit ?? paginationRef.current.perPage;
        let remote = await listYardGateSnapshot(
          query,
          requestedPage,
          requestedLimit
        );
        const lastAvailablePage = Math.max(1, remote.pagination?.lastPage ?? 1);
        if (
          remote.pagination &&
          remote.pagination.page > lastAvailablePage &&
          requestedPage !== lastAvailablePage
        ) {
          remote = await listYardGateSnapshot(
            query,
            lastAvailablePage,
            requestedLimit
          );
        }
        if (sequence !== requestSequence.current) return false;
        const nextEntries = remote.data.map(mapYardGateAppointment);
        snapshotRef.current = nextEntries;
        const nextPagination = remote.pagination ?? {
          total: remote.data.length,
          page: 1,
          perPage: requestedLimit,
          lastPage: 1,
        };
        paginationRef.current = nextPagination;
        setPagination(nextPagination);
        snapshotRangeRef.current = range;
        setSnapshotRange(range);
        const nextDockAttention = buildDockAttentionState(nextEntries);
        const hasRemoteDockChange = hasNewRemoteDockChange(
          dockAttentionRef.current,
          nextDockAttention
        );
        dockAttentionRef.current = nextDockAttention;
        if (
          hasRemoteDockChange &&
          !suppressDockAlert &&
          soundEnabledRef.current
        ) {
          playDockAlertRef.current();
        }
        const nextColumns = filterDisplayTrackedStepColumns(
          remote.trackedStepColumns
        );
        const fingerprint = yardGateSnapshotFingerprint(
          nextEntries,
          nextColumns
        );
        if (!silent || fingerprint !== snapshotFingerprint.current) {
          setSnapshot(nextEntries);
          const columnsFingerprint = JSON.stringify(nextColumns);
          if (columnsFingerprint !== trackedStepColumnsFingerprint.current) {
            setTrackedStepColumns(nextColumns);
            trackedStepColumnsFingerprint.current = columnsFingerprint;
          }
          snapshotFingerprint.current = fingerprint;
        }
        syncCursorRef.current = remote.syncCursor;
        return true;
      } catch (error) {
        if (sequence === requestSequence.current) {
          if (silent) {
            console.warn('[YardGate] Falha no refresh automático', error);
          } else {
            reportError(error);
          }
        }
        return false;
      } finally {
        if (!silent && sequence === requestSequence.current)
          setIsLoading(false);
      }
    },
    [listYardGateSnapshot]
  );

  const loadSnapshotRef = useRef(loadSnapshot);

  const syncChanges = useCallback(
    async (range: YardGateDateRange): Promise<void> => {
      const startingCursor = syncCursorRef.current;
      if (startingCursor === null) return;

      const sequence = requestSequence.current + 1;
      requestSequence.current = sequence;
      let cursor = startingCursor;
      let hasMore = false;
      let batchCount = 0;
      const merged = new Map(
        snapshotRef.current.map((entry) => [String(entry.id), entry])
      );
      let hasChanges = false;
      let requiresPageRefresh = false;
      const removeEntry = (key: string) => {
        requiresPageRefresh = true;
        merged.delete(key);
      };
      const upsertEntry = (entry: YardGateEntry) => {
        const key = String(entry.id);
        const previous = merged.get(key);
        const activeSort = sortingRef.current[0];
        const sortField = activeSort
          ? SORT_FIELD_BY_COLUMN_ID[activeSort.id]
          : 'date';
        if (
          !previous ||
          getYardGateSortValue(previous, sortField) !==
            getYardGateSortValue(entry, sortField)
        ) {
          requiresPageRefresh = true;
        }
        merged.set(key, entry);
      };

      try {
        do {
          const changes = await getYardGateChanges(
            buildYardGateQuery(filtersRef.current, sortingRef.current),
            cursor
          );
          if (sequence !== requestSequence.current) return;

          cursor = changes.cursor;
          hasMore = changes.hasMore;
          batchCount += 1;
          hasChanges ||=
            changes.upserts.length > 0 ||
            changes.removedKeys.length > 0 ||
            changes.changedOrderIds.length > 0;

          changes.removedKeys.forEach(removeEntry);
          changes.upserts.map(mapYardGateAppointment).forEach(upsertEntry);
        } while (hasMore && batchCount < 10);

        if (!hasChanges) {
          syncCursorRef.current = cursor;
          return;
        }

        if (requiresPageRefresh) {
          const previousCursor = syncCursorRef.current;
          const loaded = await loadSnapshot(range, {
            silent: true,
            page: paginationRef.current.page,
            limit: paginationRef.current.perPage,
          });
          if (!loaded) syncCursorRef.current = previousCursor;
          return;
        }

        const nextEntries = [...merged.values()];
        snapshotRef.current = nextEntries;

        const nextDockAttention = buildDockAttentionState(nextEntries);
        const hasRemoteDockChange = hasNewRemoteDockChange(
          dockAttentionRef.current,
          nextDockAttention
        );
        dockAttentionRef.current = nextDockAttention;
        if (hasRemoteDockChange && soundEnabledRef.current) {
          playDockAlertRef.current();
        }

        const fingerprint = yardGateSnapshotFingerprint(
          nextEntries,
          trackedStepColumns
        );
        if (fingerprint !== snapshotFingerprint.current) {
          setSnapshot(nextEntries);
          snapshotFingerprint.current = fingerprint;
        }

        syncCursorRef.current = cursor;
      } catch (error) {
        if (sequence === requestSequence.current) {
          console.warn('[YardGate] Falha no refresh incremental', error);
        }
      }
    },
    [getYardGateChanges, loadSnapshot, trackedStepColumns]
  );

  const syncChangesRef = useRef(syncChanges);

  useEffect(() => {
    snapshotRangeRef.current = snapshotRange;
  }, [snapshotRange]);

  useEffect(() => {
    isBusyRef.current = isLoading || isSaving || busyEntryId !== null;
  }, [isLoading, isSaving, busyEntryId]);

  useEffect(() => {
    loadSnapshotRef.current = loadSnapshot;
  }, [loadSnapshot]);

  useEffect(() => {
    syncChangesRef.current = syncChanges;
  }, [syncChanges]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    playDockAlertRef.current = playDockAlert;
  }, [playDockAlert]);

  const trackRefresh = useCallback((operation: Promise<unknown>) => {
    const tracked = operation.then(() => undefined);
    refreshPromiseRef.current = tracked;
    const release = () => {
      if (refreshPromiseRef.current === tracked) {
        refreshPromiseRef.current = null;
      }
    };
    void tracked.then(release, release);
    return tracked;
  }, []);

  const runSilentRefresh = useCallback(async () => {
    if (
      journeyMutationInFlightRef.current ||
      refreshPromiseRef.current ||
      isBusyRef.current
    )
      return;
    if (document.visibilityState === 'hidden') return;

    const range = snapshotRangeRef.current;
    if (!range) return;

    await trackRefresh(syncChangesRef.current(range));
  }, [trackRefresh]);

  useInterval(() => {
    void runSilentRefresh();
  }, YARD_GATE_REFRESH_INTERVAL_MS);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runSilentRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [runSilentRefresh]);

  useEffect(() => {
    void loadSnapshot({
      dateFrom: initialFilters.dateFrom,
      dateTo: initialFilters.dateTo,
    });
    return () => {
      requestSequence.current += 1;
    };
  }, [initialFilters.dateFrom, initialFilters.dateTo, loadSnapshot]);

  useEffect(() => {
    let isMounted = true;

    const loadCatalogs = async () => {
      setIsCatalogLoading(true);
      try {
        const loadedCatalogs = await getYardGateCatalogs();
        if (isMounted) setCatalogs(loadedCatalogs);
      } catch (error) {
        if (isMounted) reportError(error);
      } finally {
        if (isMounted) setIsCatalogLoading(false);
      }
    };

    const loadAuthorizers = async () => {
      try {
        const loadedAuthorizers = await getYardGateAuthorizers();
        if (isMounted) setAuthorizers(loadedAuthorizers);
      } catch (error) {
        if (isMounted) reportError(error);
      }
    };

    const loadWarehouses = async () => {
      try {
        const params = { orderBy: 'tradeName', direction: 'asc', limit: 100 };
        const first = await api.get<
          | ListedWarehouse[]
          | { data: ListedWarehouse[]; meta?: { lastPage?: number } }
        >('warehouses', { params: { ...params, page: 1 } });
        const firstItems = Array.isArray(first.data)
          ? first.data
          : first.data.data;
        const lastPage = Array.isArray(first.data)
          ? 1
          : (first.data.meta?.lastPage ?? 1);
        const items = [...firstItems];

        for (let page = 2; page <= lastPage; page += 1) {
          const response = await api.get<
            ListedWarehouse[] | { data: ListedWarehouse[] }
          >('warehouses', { params: { ...params, page } });
          const pageItems = Array.isArray(response.data)
            ? response.data
            : response.data.data;
          items.push(...pageItems);
        }

        if (isMounted) {
          setWarehouseOptions(
            items.map(({ id, tradeName, documentType, document }) => ({
              value: id,
              label: tradeName,
              document: Formatter.document(document, documentType),
            }))
          );
        }
      } catch (error) {
        if (isMounted) reportError(error);
      }
    };

    void loadCatalogs();
    void loadAuthorizers();
    void loadWarehouses();
    return () => {
      isMounted = false;
    };
  }, [getYardGateCatalogs, getYardGateAuthorizers]);

  const applyFilters = async (nextFilters: YardGateFilters) => {
    if (Object.keys(validateYardGateDateRange(nextFilters)).length > 0) return;

    const nextRange = {
      dateFrom: nextFilters.dateFrom,
      dateTo: nextFilters.dateTo,
    };
    const loaded = await loadSnapshot(nextRange, {
      filters: nextFilters,
      page: 1,
    });
    if (!loaded) return;

    filtersRef.current = nextFilters;
    setFilters(nextFilters);
  };

  const clearFilters = () => {
    const nextFilters = {
      ...DEFAULT_YARD_GATE_LOCAL_FILTERS,
      dateFrom: filtersRef.current.dateFrom,
      dateTo: filtersRef.current.dateTo,
    };
    void applyFilters(nextFilters);
  };

  const changePage = ({ page, limit }: { page: number; limit: number }) => {
    const range = snapshotRangeRef.current ?? filtersRef.current;
    void loadSnapshot(range, { page, limit });
  };

  const changeSorting = (updater: Updater<SortingState>) => {
    const requested =
      typeof updater === 'function' ? updater(sortingRef.current) : updater;
    const nextSorting =
      requested[0] && !SORT_FIELD_BY_COLUMN_ID[requested[0].id]
        ? []
        : requested.slice(0, 1);
    sortingRef.current = nextSorting;
    setSorting(nextSorting);
    const range = snapshotRangeRef.current ?? filtersRef.current;
    void loadSnapshot(range, { page: 1 });
  };

  const createAuthorizer = async (
    name: string
  ): Promise<YardGateOption | null> => {
    try {
      const created = await createYardGateAuthorizer(name);
      setAuthorizers((current) =>
        current.some((option) => option.value === created.value)
          ? current
          : [...current, created]
      );
      return created;
    } catch (error) {
      reportError(error);
      return null;
    }
  };

  const saveEntry = async (input: CreateYardGateEntryInput) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const created = await createStandaloneYardGateEntry({
        date: input.date,
        time: input.time,
        driverCpf: input.driverCpf,
        driverName: input.driverName,
        phone: input.phone || undefined,
        observation: input.observation || undefined,
        orderTypeId: input.orderTypeId,
        carrierId: input.carrierId,
        vehicleTypeId: input.vehicleTypeId,
        plate: input.plate,
        companyId: null,
        areaId: input.areaId,
        entryAuthorizedById: input.authorizerId,
      });

      let attachmentUploadFailed = false;
      if (input.attachments.length) {
        try {
          await uploadYardGateChecklistAttachments(
            created.type,
            created.sourceId,
            input.attachments
          );
        } catch {
          attachmentUploadFailed = true;
        }
      }

      notify(
        attachmentUploadFailed ? 'error' : 'success',
        attachmentUploadFailed
          ? 'Entrada criada, mas não foi possível enviar os anexos'
          : 'Entrada cadastrada com sucesso'
      );
      setActiveTab(0);

      const nextFilters: YardGateFilters = {
        ...DEFAULT_YARD_GATE_LOCAL_FILTERS,
        statuses: ['AWAITING_RELEASE'],
        dateFrom: input.date,
        dateTo: input.date,
      };
      const nextRange = {
        dateFrom: input.date,
        dateTo: input.date,
      };
      const loaded = await loadSnapshot(nextRange, {
        filters: nextFilters,
        page: 1,
      });
      if (loaded) {
        filtersRef.current = nextFilters;
        setFilters(nextFilters);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintOrder = useCallback((entry: YardGateEntry) => {
    if (entry.orderId == null) return;
    window.open(`/agendamento/${entry.orderId}/impressao`, '_blank');
  }, []);

  const changeStatus = useCallback(
    async (
      type: YardGateAppointmentType,
      sourceId: number,
      status: YardGateMutableStatus
    ) => {
      if (busyEntryId !== null) return;

      setBusyEntryId(`${type}-${sourceId}`);
      try {
        await updateYardGateStatus(type, sourceId, status);
        notify(
          'success',
          status === 'ENTRY_AUTHORIZED'
            ? 'Entrada autorizada com sucesso'
            : 'Saída autorizada com sucesso'
        );
        if (snapshotRange) await loadSnapshot(snapshotRange);
      } catch (error) {
        reportError(error);
      } finally {
        setBusyEntryId(null);
      }
    },
    [busyEntryId, snapshotRange, updateYardGateStatus, loadSnapshot]
  );

  const savePhone = useCallback(
    async (entry: YardGateEntry, phone: string | null): Promise<boolean> => {
      if (busyEntryId !== null) return false;

      setBusyEntryId(entry.id);
      try {
        await updateYardGateContact(entry.type, entry.sourceId, { phone });
        notify('success', 'Telefone atualizado com sucesso');
        if (snapshotRange) await loadSnapshot(snapshotRange);
        return true;
      } catch (error) {
        reportError(error);
        return false;
      } finally {
        setBusyEntryId(null);
      }
    },
    [busyEntryId, snapshotRange, updateYardGateContact, loadSnapshot]
  );

  const saveDocks = useCallback(
    async (entry: YardGateEntry, docks: number[]): Promise<boolean> => {
      if (busyEntryId !== null) return false;

      setBusyEntryId(entry.id);
      try {
        await updateYardGateDocks(entry.type, entry.sourceId, { docks });
        notify('success', 'Doca atualizada com sucesso');
        if (snapshotRange)
          await loadSnapshot(snapshotRange, { suppressDockAlert: true });
        return true;
      } catch (error) {
        reportError(error);
        return false;
      } finally {
        setBusyEntryId(null);
      }
    },
    [busyEntryId, snapshotRange, updateYardGateDocks, loadSnapshot]
  );

  const saveObservation = async (
    entry: YardGateEntry,
    observation: string | null
  ): Promise<boolean> => {
    if (busyEntryId !== null) return false;

    setBusyEntryId(entry.id);
    try {
      await updateYardGateObservation(entry.type, entry.sourceId, {
        observation,
      });
      notify('success', 'Anotação atualizada com sucesso');
      if (snapshotRange) await loadSnapshot(snapshotRange);
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setBusyEntryId(null);
    }
  };

  const loadChecklistAttachments = useCallback(
    (entry: YardGateEntry): Promise<YardGateChecklistAttachmentsDto> =>
      listYardGateChecklistAttachments(entry.type, entry.sourceId),
    [listYardGateChecklistAttachments]
  );

  const saveChecklist = async (
    entry: YardGateEntry,
    files: File[]
  ): Promise<boolean> => {
    if (busyEntryId !== null) return false;

    setBusyEntryId(entry.id);
    try {
      await uploadYardGateChecklistAttachments(
        entry.type,
        entry.sourceId,
        files
      );
      notify('success', 'Anexos enviados com sucesso');
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setBusyEntryId(null);
    }
  };

  const openChecklist = async (
    entry: YardGateEntry,
    attachment: YardGateChecklistAttachmentDto
  ): Promise<boolean> => {
    if (busyEntryId !== null) return false;

    setBusyEntryId(entry.id);
    try {
      const { url } = await getYardGateChecklistAttachmentUrl(
        entry.type,
        entry.sourceId,
        attachment.id
      );
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setBusyEntryId(null);
    }
  };

  const deleteChecklist = async (
    entry: YardGateEntry,
    attachment: YardGateChecklistAttachmentDto
  ): Promise<boolean> => {
    if (busyEntryId !== null) return false;

    setBusyEntryId(entry.id);
    try {
      await deleteYardGateChecklistAttachment(
        entry.type,
        entry.sourceId,
        attachment.id
      );
      notify('success', 'Anexo excluído com sucesso');
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setBusyEntryId(null);
    }
  };

  const refreshSnapshotAfterJourney = () => {
    const activeRefresh = refreshPromiseRef.current;
    const reconciliation = (activeRefresh ?? Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        const range = snapshotRangeRef.current ?? filtersRef.current;
        await loadSnapshot(range, {
          silent: true,
          page: paginationRef.current.page,
          limit: paginationRef.current.perPage,
        });
      });
    trackRefresh(reconciliation);
  };

  const confirmStartJourney = async (orderId: number, yardFlowId: number) => {
    journeyMutationInFlightRef.current = true;
    try {
      const confirmed = await confirmYardGateJourney(orderId, yardFlowId);
      refreshSnapshotAfterJourney();
      return confirmed;
    } finally {
      journeyMutationInFlightRef.current = false;
    }
  };

  const saveStartJourney = async (
    entry: YardGateEntry,
    params: {
      currentStepId: number;
      fieldAnswers: YardJourneyFieldAnswerInput[];
    }
  ): Promise<boolean> => {
    if (busyEntryId !== null || entry.orderId == null) return false;

    setBusyEntryId(entry.id);
    journeyMutationInFlightRef.current = true;
    try {
      await completeYardGatePortariaStep(entry.orderId, {
        expectedStepId: params.currentStepId,
        fieldAnswers: params.fieldAnswers,
      });
      notify('success', 'Jornada iniciada com sucesso');
      refreshSnapshotAfterJourney();
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      journeyMutationInFlightRef.current = false;
      setBusyEntryId(null);
    }
  };

  const confirmJourneyFlow = () => {
    notify('success', 'Jornada iniciada com sucesso');
  };

  const manageChecklist = useCallback(
    (entry: YardGateEntry) => setChecklistEntry(entry),
    []
  );

  const exportEntries = async (exportFormat: YardGateExportFormat) => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const query = buildYardGateQuery(filtersRef.current, sortingRef.current);
      const firstPage = await listYardGateSnapshot(query, 1, 100);
      const orderedEntries = firstPage.data.map(mapYardGateAppointment);
      for (let page = 2; page <= firstPage.pagination.lastPage; page += 1) {
        const nextPage = await listYardGateSnapshot(query, page, 100);
        orderedEntries.push(...nextPage.data.map(mapYardGateAppointment));
      }
      const blob =
        exportFormat === 'xlsx'
          ? exportYardGateEntriesXlsx(orderedEntries, trackedStepColumns)
          : exportYardGateEntries(orderedEntries, trackedStepColumns);
      FileSaver.saveAs(
        blob,
        `entradas-portaria-${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`
      );
    } catch (error) {
      reportError(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Scaffold>
      <S.MainPanel>
        <ListingPageHeader
          icon={<S.SettingsIcon />}
          title="Portaria"
          isLoading={isLoading || isSaving || busyEntryId !== null}
        />
        <S.ContentCard>
          <TabsController activeTab={activeTab} onTabChange={setActiveTab}>
            {[
              <ModalTab key="entries" title="Entradas" />,
              <ModalTab key="new-entry" title="Entrada Avulsa" />,
            ]}
          </TabsController>
          <S.TabContent>
            {activeTab === 0 ? (
              <>
                <EntryFilters
                  value={filters}
                  catalogs={catalogs}
                  warehouseOptions={warehouseOptions}
                  isCatalogLoading={isCatalogLoading}
                  onApply={applyFilters}
                  onClear={clearFilters}
                  rightActions={
                    <>
                      <StatusLegend />
                      {tableInstance && (
                        <DataTableViewOptions
                          table={tableInstance}
                          ordering
                          defaultColumnPinning={
                            YARD_GATE_DEFAULT_COLUMN_PINNING
                          }
                          triggerClassName="h-9 rounded-md border-slate-300 bg-white px-4 text-xs text-slate-700 hover:bg-gray-50 hover:text-slate-800"
                        />
                      )}
                      <ExportMenu
                        disabled={isExporting}
                        onExport={exportEntries}
                      />
                    </>
                  }
                />
                <EntriesTable
                  entries={visibleEntries}
                  trackedStepColumns={trackedStepColumns}
                  isBusy={busyEntryId !== null}
                  isLoading={isLoading && snapshot.length === 0}
                  pageSize={pagination.perPage}
                  pageSizeOptions={YARD_GATE_PAGE_SIZE_OPTIONS}
                  pagination={pagination}
                  manualPagination
                  onPaginationChange={changePage}
                  manualSorting
                  sorting={sorting}
                  onSortingChange={changeSorting}
                  dockOptions={catalogs.docks}
                  onPrintOrder={handlePrintOrder}
                  onTableInstance={rememberTableInstance}
                  onStatusChange={changeStatus}
                  onUpdateDocks={saveDocks}
                  onUpdatePhone={savePhone}
                  onAddObservation={setObservationEntry}
                  onManageChecklist={manageChecklist}
                  onStartJourney={setStartJourneyEntry}
                />
              </>
            ) : (
              <EntryForm
                catalogs={catalogs}
                authorizers={authorizers}
                isSaving={isSaving}
                isCatalogLoading={isCatalogLoading}
                onSubmit={saveEntry}
                onCreateAuthorizer={createAuthorizer}
                onCancel={() => setActiveTab(0)}
              />
            )}
          </S.TabContent>
        </S.ContentCard>
      </S.MainPanel>
      {observationEntry ? (
        <ObservationModal
          entry={observationEntry}
          isBusy={busyEntryId !== null}
          onClose={() => setObservationEntry(null)}
          onSubmit={saveObservation}
        />
      ) : null}
      {checklistEntry ? (
        <ChecklistModal
          entry={checklistEntry}
          isBusy={busyEntryId !== null}
          onClose={() => setChecklistEntry(null)}
          onLoad={loadChecklistAttachments}
          onUpload={saveChecklist}
          onOpen={openChecklist}
          onDelete={deleteChecklist}
        />
      ) : null}
      {startJourneyEntry ? (
        <StartJourneyModal
          entry={startJourneyEntry}
          isBusy={busyEntryId !== null}
          onClose={() => setStartJourneyEntry(null)}
          getJourney={getYardGateJourney}
          confirmJourney={confirmStartJourney}
          onSubmit={saveStartJourney}
          onFlowConfirmed={confirmJourneyFlow}
        />
      ) : null}
    </Scaffold>
  );
};
